import { Injectable, Inject } from '@angular/core';
import { HttpParams, HttpHeaders, HttpClient } from '@angular/common/http';
import { ConfigService } from '../config/config.service';
import { Observable } from 'rxjs';
import { AuthService } from '../guards/auth.service';
import * as _ from 'lodash';

@Injectable()
export class HttpService {
  private API_URL = '';

  constructor(
    protected httpClient: HttpClient,
    protected config: ConfigService,
    protected authService: AuthService,
    @Inject(String) private resource: string
  ) {
    if (this.config !== undefined) {
      this.API_URL = this.config.environnement.apiUrl;
    }
  }

  public invokeGet<T>(
    route?: string,
    params?: {},
    pagingParameters?: {}
  ): Observable<T> {
    const url = `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`;
    let httpParams = new HttpParams();

    if (params) {
      for (const param of Object.keys(params)) {
        if (!_.isNil(params[param])) {
          if (_.isArray(params[param])) {
            for (const paramValue of params[param]) {
              httpParams = httpParams.append(param, paramValue);
            }
          } else {
            httpParams = httpParams.append(param, params[param]);
          }
        }
      }
    }

    if (pagingParameters) {
      for (const param of Object.keys(pagingParameters)) {
        if (pagingParameters[param] && !_.isNil(pagingParameters[param])) {
          httpParams = httpParams.set(param, pagingParameters[param]);
        }
      }
    }

    return this.httpClient.get<any>(url, {
      headers: this.headers,
      observe: 'body',
      params: httpParams,
      withCredentials: false,
    });
  }

  public invokeGetFile(route?: string, params?: {}): Observable<Blob> {
    const url = `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`;

    return this.httpClient.get<Blob>(url, {
      headers: this.headers,
      reportProgress: true,
      observe: 'body',
      params: { ...params },
      withCredentials: false,
      responseType: 'blob' as 'json',
    });
  }

  public invokeGetMultipleFiles(
    route?: string,
    params?: any[]
  ): Observable<Blob> {
    const url = `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`;
    let body;
    if (params) {
      body = params.map(function (param) {
        let invoiceIdEntry = { Param: param };
        return invoiceIdEntry;
      });
    }

    return this.httpClient.post<Blob>(url, body, {
      headers: this.headers,
      reportProgress: true,
      observe: 'body',
      withCredentials: false,
      responseType: 'blob' as 'json',
    });
  }

  public invokePost(element: any, route?: string): Observable<any> {
    return this.httpClient.post<any>(
      `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`,
      element,
      {
        headers: this.headers,
        observe: 'body',
        withCredentials: false,
      }
    );
  }

  public invokeUploadFile(
    file: File,
    element?: any,
    route?: string
  ): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    if (!_.isNil(element)) {
      for (const [key, value] of Object.entries(element)) {
        if (!_.isNil(value)) {
          formData.append(key, `${value}`);
        }
      }
    }

    return this.httpClient.post<any>(
      `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`,
      formData,
      {
        headers: this.headers,
        observe: 'body',
        withCredentials: false,
      }
    );
  }

  public invokeUploadFiles(files: File[], route?: string): Observable<any> {
    const formData: FormData = new FormData();
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return this.httpClient.post<any>(
      `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`,
      formData,
      {
        headers: this.headers,
        observe: 'body',
        withCredentials: false,
      }
    );
  }

  public invokePut<T>(
    element: any,
    route?: string,
    params?: {}
  ): Observable<T> {
    return this.httpClient.put<T>(
      `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`,
      element,
      {
        headers: this.headers,
        observe: 'body',
        params: { ...params },
        withCredentials: false,
      }
    );
  }

  public invokeDelete(route?: string, params?: {}): Observable<any> {
    return this.httpClient.delete<any>(
      `${this.API_URL}/${this.resource + (route ? '/' + route : '')}`,
      {
        headers: this.headers,
        observe: 'body',
        params: { ...params },
        withCredentials: false,
      }
    );
  }

  public getById<T>(id: number | string): Observable<T> {
    id = id || '';
    return this.invokeGet<T>(String(id), null);
  }

  public deleteById(id: string): Observable<any> {
    return this.httpClient.delete<any>(
      `${this.API_URL}/${this.resource}/${id}`,
      {
        headers: this.headers,
        observe: 'body',
        withCredentials: false,
      }
    );
  }

  public updateById(element: any, id: number): Observable<any> {
    return this.httpClient.put<any>(
      `${this.API_URL}/${this.resource}/${id}`,
      element,
      {
        headers: this.headers,
        observe: 'body',
        withCredentials: false,
      }
    );
  }

  private get headers(): HttpHeaders {
    return this.authService.getIdToken()
      ? this.buildAuthHeaders(this.authService.getIdToken())
      : new HttpHeaders();
  }

  private buildAuthHeaders(idToken: string) {
    let headers = new HttpHeaders().append(
      'Authorization',
      `Bearer ${idToken}`
    );
    return headers;
  }
}
